/** Table extraction / formatting helpers for scheduled-task run results. */

export const NON_DATA_KEYS = new Set([
  '_meta',
  'meta',
  '__typename',
  'columns',
  'rows',
  'query',
  'report',
  'reportUrl',
  'report_url',
  'icon',
  'icon_url',
  'error',
  'message',
  'status',
])

export function formatLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim()
}

export function isLikelyIdentifierKey(key: string): boolean {
  const kl = String(key).toLowerCase()
  return (
    /(^|_)(id|uuid|guid|sku|asin|isbn)$/.test(kl) ||
    kl.endsWith('_id') ||
    kl === 'customer id' ||
    kl === 'customerid'
  )
}

export function formatValue(value: any, key: string): string {
  if (value === null || value === undefined) return '—'
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (value.name !== undefined) return formatValue(value.name, key)
    if (value.value !== undefined) return formatValue(value.value, key)
    if (value.title !== undefined) return formatValue(value.title, key)
    if (value.label !== undefined) return formatValue(value.label, key)
    if (value.text !== undefined) return formatValue(value.text, key)
    if (value.id !== undefined) return formatValue(value.id, key)
    const keys = Object.keys(value)
    if (keys.length <= 2) return keys.map((k) => `${k}: ${value[k]}`).join(', ')
    return `${keys.length} fields`
  }
  if (Array.isArray(value)) return value.length ? `${value.length} items` : '—'
  const kl = String(key).toLowerCase()
  const n = typeof value === 'number' ? value : Number(value)
  if (!isNaN(n) && typeof value !== 'string') {
    if (isLikelyIdentifierKey(key)) return String(Math.trunc(n))
    const label = kl.replace(/_/g, ' ')
    if (/\b(total rows|row count|returned rows)\b/.test(label)) {
      return n.toLocaleString()
    }
    if (/revenue|amount|price|sales|\bvalue\b|spend|cost|\bcpa\b/.test(kl) || /\btotal\b/.test(label)) {
      return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    }
    if (/\broas\b/.test(kl)) {
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
    if (/rate|percent|ratio|ctr|bounce|conversion[_\s-]?rate/.test(kl)) {
      return `${(n * 100).toFixed(1)}%`
    }
    return n.toLocaleString()
  }
  return String(value)
}

function columnNumericValues(rows: Record<string, any>[], key: string): number[] {
  const out: number[] = []
  for (const r of rows) {
    const raw = r[key]
    if (raw === null || raw === undefined) continue
    const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/,/g, ''))
    if (!Number.isFinite(n)) return []
    out.push(n)
  }
  return out
}

function isAdditiveMeasureColumn(key: string, rows: Record<string, any>[]): boolean {
  if (!rows.length || isLikelyIdentifierKey(key)) return false
  const kl = key.toLowerCase().replace(/\s+/g, '_')
  const measure =
    /\b(revenue|spend|cost|sales|profit|margin|subtotal|tax|fee|discount)\b/.test(kl) ||
    /\b(impressions?|clicks?|conversions?|orders?|units|quantity|volume|sessions?|visitors?|users?)\b/.test(
      kl,
    ) ||
    (/^total_/.test(kl) && !/total_rows|total_count/.test(kl)) ||
    kl === 'amount' ||
    kl === 'value'
  const countLike = /\bcount\b/.test(kl) || kl === 'cnt' || kl === 'n'
  if (countLike) {
    if (rows.length <= 1) return false
  } else if (!measure) {
    return false
  }
  const nums = columnNumericValues(rows, key)
  return nums.length === rows.length && nums.length > 0
}

export function buildMetrics(rows: Record<string, any>[]): { label: string; value: string }[] {
  if (!rows?.length) return []
  const first = rows[0]
  if (!first || typeof first !== 'object') return []

  const metrics: { label: string; value: string }[] = []
  const seen = new Set<string>()

  for (const key of Object.keys(first)) {
    if (seen.has(key) || NON_DATA_KEYS.has(key.toLowerCase())) continue
    if (!isAdditiveMeasureColumn(key, rows)) continue
    const nums = columnNumericValues(rows, key)
    const sum = nums.reduce((a, b) => a + b, 0)
    seen.add(key)
    metrics.push({ label: `Total ${formatLabel(key)}`, value: formatValue(sum, key) })
  }

  if (rows.length > 5 && metrics.length === 0) {
    metrics.unshift({ label: 'Rows in this result', value: rows.length.toLocaleString() })
  }

  return metrics.slice(0, 6)
}

export function safeParse(str: string): any {
  const s = str.trim()
  try {
    return JSON.parse(s)
  } catch {
    /* continue */
  }
  for (let i = 1; i <= 5 && i < s.length; i++) {
    try {
      return JSON.parse(s.slice(0, -i))
    } catch {
      /* continue */
    }
  }
  return null
}

export function unwrapResponseData(raw: any): any {
  if (raw == null) return null

  let data: any = raw
  if (typeof data === 'string') {
    data = safeParse(data)
    if (data == null) return null
  }

  if (data && typeof data === 'object' && 'output' in data) {
    const inner = data.output
    if (typeof inner === 'string') {
      const parsed = safeParse(inner)
      if (parsed != null) data = parsed
      else return null
    } else if (typeof inner === 'object' && inner != null) {
      data = inner
    }
  }

  if (data && typeof data === 'object' && !Array.isArray(data) && 'output' in data) {
    const inner2 = data.output
    if (typeof inner2 === 'string') {
      const parsed2 = safeParse(inner2)
      if (parsed2 != null) data = parsed2
    } else if (typeof inner2 === 'object' && inner2 != null) {
      data = inner2
    }
  }

  return data
}

function convertColumnsRowsToObjects(columns: string[], rows: any[][]): Record<string, any>[] {
  if (!columns?.length || !Array.isArray(rows) || rows.length === 0) return []
  const first = rows[0]
  if (first != null && typeof first === 'object' && !Array.isArray(first)) {
    return rows as Record<string, any>[]
  }
  return rows.map((row) => {
    if (!Array.isArray(row)) return row as Record<string, any>
    const obj: Record<string, any> = {}
    columns.forEach((col, i) => {
      obj[col] = row[i]
    })
    return obj
  })
}

function flattenMcpRow(item: any): Record<string, any> | null {
  if (!item || typeof item !== 'object' || !item.metadata || typeof item.metadata !== 'object') {
    return null
  }
  const out: Record<string, any> = {}
  for (const [key, value] of Object.entries(item.metadata)) {
    if (value === null || value === undefined) continue
    if (typeof value === 'number' || typeof value === 'boolean') out[key] = value
    else if (typeof value === 'string') {
      out[key] = /^-?[\d,]+(\.\d+)?$/.test(value) ? parseFloat(value.replace(/,/g, '')) : value
      if (key === 'date_trunc') out.date = value.split('T')[0]
    } else if (Array.isArray(value)) {
      out[key] =
        value.length && (typeof value[0] === 'string' || typeof value[0] === 'number')
          ? value
          : `${value.length} items`
    } else if (typeof value === 'object') {
      const n = value as Record<string, any>
      if (n.value !== undefined) out[key] = n.value
      else if (n.name !== undefined) out[key] = n.name
      else {
        Object.entries(n).forEach(([nk, nv]) => {
          if (nv !== null && typeof nv !== 'object') out[`${key}_${nk}`] = nv
        })
      }
    }
  }
  if (item.title && !out.title && !out.name) out._title = item.title
  return Object.keys(out).length ? out : null
}

export function normalizeRows(rows: Record<string, any>[]): Record<string, any>[] {
  if (!rows?.length || !rows[0] || typeof rows[0] !== 'object') return rows
  const first = rows[0]
  const isMcp = 'id' in first && 'title' in first && 'metadata' in first
  if (!isMcp) return rows
  const flattened = rows.map(flattenMcpRow).filter(Boolean) as Record<string, any>[]
  return flattened.length ? flattened : rows
}

function isApiErrorNoiseRow(row: Record<string, any>): boolean {
  const keys = Object.keys(row).filter((k) => row[k] !== null && row[k] !== undefined)
  if (keys.length === 0 || keys.length > 8) return false

  const codeRaw = row.details_code ?? row.detailsCode ?? row.code ?? row.status
  const codeNum = typeof codeRaw === 'number' ? codeRaw : Number(codeRaw)
  const hasHttpErrorCode = Number.isFinite(codeNum) && codeNum >= 400 && codeNum < 600

  const titleStr = row.title != null ? String(row.title).trim() : ''
  const errorishTitle = /^error$/i.test(titleStr)

  if (
    errorishTitle &&
    keys.every((k) => {
      const v = row[k]
      return v === null || v === undefined || v === '' || k === 'title' || k === '_title'
    })
  ) {
    return true
  }

  const errorishKey = (k: string) => {
    const lk = k.toLowerCase()
    return (
      lk === 'details_code' ||
      lk === 'title' ||
      lk === 'message' ||
      lk === 'code' ||
      lk === 'status' ||
      lk === 'error' ||
      lk === '_title' ||
      lk === 'details'
    )
  }
  const onlyErrorishKeys = keys.every(errorishKey)

  if (onlyErrorishKeys && (hasHttpErrorCode || errorishTitle)) return true
  if (hasHttpErrorCode && errorishTitle && keys.length <= 5) return true
  return false
}

export function filterApiErrorNoiseRows(rows: Record<string, any>[]): Record<string, any>[] {
  return rows.filter((r) => !isApiErrorNoiseRow(r))
}

export function extractRows(data: any): Record<string, any>[] {
  if (!data || typeof data !== 'object') return []

  if (Array.isArray(data)) {
    if (data.length > 0 && data[0] != null && typeof data[0] === 'object' && !Array.isArray(data[0])) {
      return data
    }
    return []
  }

  if (
    data.columns &&
    Array.isArray(data.columns) &&
    data.rows &&
    Array.isArray(data.rows) &&
    data.rows.length > 0
  ) {
    const firstRow = data.rows[0]
    if (Array.isArray(firstRow)) return convertColumnsRowsToObjects(data.columns, data.rows)
    if (typeof firstRow === 'object') return data.rows as Record<string, any>[]
  }

  const arrayFields = ['results', 'data', 'rows', 'items', 'records', 'metrics']
  for (const field of arrayFields) {
    const arr = data[field]
    if (Array.isArray(arr) && arr.length > 0 && typeof arr[0] === 'object' && !Array.isArray(arr[0])) {
      return arr
    }
  }

  const firstArr = Object.values(data).find(
    (v) => Array.isArray(v) && v.length > 0 && typeof v[0] === 'object',
  ) as Record<string, any>[] | undefined
  if (firstArr) return firstArr
  if (Object.keys(data).length > 0) return [data]
  return []
}

export type ExtractedRunTable = {
  id: string
  tool_name: string
  rows: Record<string, any>[]
}

export type RunHighlightMetric = {
  key: string
  label: string
  value: string
}

function tableColumnKeySignature(rows: Record<string, any>[]): string {
  if (!rows.length || !rows[0] || typeof rows[0] !== 'object') return ''
  return Object.keys(rows[0]).sort().join('|')
}

function tableColumnSignature(rows: Record<string, any>[]): string {
  if (!rows.length || !rows[0] || typeof rows[0] !== 'object') return ''
  return `${tableColumnKeySignature(rows)}::${rows.length}`
}

function isDisplayableTable(rows: Record<string, any>[]): boolean {
  if (!rows.length || !rows[0] || typeof rows[0] !== 'object' || Array.isArray(rows[0])) return false
  const keys = Object.keys(rows[0]).filter((k) => !NON_DATA_KEYS.has(k.toLowerCase()))
  if (keys.length === 0) return false
  if (rows.length === 1 && keys.length < 2) return false
  return true
}

const PAGINATION_KEYS = new Set([
  'offset',
  'limit',
  'has_more',
  'next_offset',
  'returned_rows',
  'gid',
])

/** Sheet pagination / tool-config blobs, not merchant result tables. */
export function isConfigOrPaginationTable(rows: Record<string, any>[]): boolean {
  if (!rows?.length || !rows[0] || typeof rows[0] !== 'object' || Array.isArray(rows[0])) return false
  const keys = Object.keys(rows[0]).map((k) => k.toLowerCase())
  const keySet = new Set(keys)
  let hits = 0
  for (const k of PAGINATION_KEYS) {
    if (keySet.has(k)) hits += 1
  }
  if (hits >= 2) return true
  if (rows.length === 1 && keySet.has('url') && keySet.has('headers')) return true
  return false
}

function sheetDataRowsFromUnwrapped(unwrapped: any): Record<string, any>[] | null {
  const results = Array.isArray(unwrapped?.results)
    ? unwrapped.results
    : Array.isArray(unwrapped)
      ? unwrapped
      : null
  if (!results?.length) return null
  const all: Record<string, any>[] = []
  for (const item of results) {
    if (!item || typeof item !== 'object') continue
    const nested = item?.metadata?.rows
    if (
      Array.isArray(nested) &&
      nested.length > 0 &&
      nested[0] &&
      typeof nested[0] === 'object' &&
      !Array.isArray(nested[0])
    ) {
      all.push(...(nested as Record<string, any>[]))
    }
  }
  return all.length ? all : null
}

function rowsFromToolOutput(output: unknown): Record<string, any>[] {
  if (output == null) return []
  const unwrapped = unwrapResponseData(typeof output === 'string' ? { output } : output)
  if (unwrapped == null) return []
  const sheetRows = sheetDataRowsFromUnwrapped(unwrapped)
  if (sheetRows) return filterApiErrorNoiseRows(normalizeRows(sheetRows))
  return filterApiErrorNoiseRows(normalizeRows(extractRows(unwrapped)))
}

/**
 * Collect every distinct tabular result from a run (agent steps + export primary).
 * Skips sheet pagination / config metadata. Does not parse summary prose into tables.
 */
export function extractAllTablesFromResponseData(responseData: any): ExtractedRunTable[] {
  if (!responseData || typeof responseData !== 'object') return []

  const out: ExtractedRunTable[] = []
  const seen = new Set<string>()

  const push = (toolName: string, rows: Record<string, any>[], iteration?: number) => {
    if (!isDisplayableTable(rows)) return
    if (isConfigOrPaginationTable(rows)) return
    const name = String(toolName || 'data')
    const colSig = tableColumnKeySignature(rows)
    const existing = out.find((t) => t.tool_name === name && tableColumnKeySignature(t.rows) === colSig)
    if (existing) {
      existing.rows = existing.rows.concat(rows)
      return
    }
    const sig = `${name}::${colSig}`
    if (seen.has(sig)) return
    seen.add(sig)
    out.push({
      id: `${name}-${iteration ?? out.length}`,
      tool_name: name,
      rows,
    })
  }

  const primary = responseData.export?.primary
  const primaryRows = primary?.rows?.length
    ? filterApiErrorNoiseRows(normalizeRows(primary.rows as Record<string, any>[]))
    : []
  const primaryUsable = primaryRows.length > 0 && !isConfigOrPaginationTable(primaryRows)
  if (primaryUsable) {
    push(primary.tool_name || 'data', primaryRows, primary.iteration)
  }

  const agentSteps = Array.isArray(responseData.agent_steps) ? responseData.agent_steps : []
  for (const step of agentSteps) {
    if (step?.success === false) continue
    const toolName = String(step?.tool_name || 'data')
    if (primaryUsable && toolName === String(primary.tool_name || 'data')) continue
    const rows = rowsFromToolOutput(step?.tool_output)
    push(toolName, rows, Number(step?.iteration) || undefined)
  }

  return out.slice(0, 8)
}

/** Pagination / sheet-config blobs for the collapsed run-details section. */
export function extractConfigTablesFromResponseData(responseData: any): ExtractedRunTable[] {
  if (!responseData || typeof responseData !== 'object') return []
  const out: ExtractedRunTable[] = []
  const seen = new Set<string>()

  const push = (toolName: string, rows: Record<string, any>[], iteration?: number) => {
    if (!isConfigOrPaginationTable(rows)) return
    const name = String(toolName || 'data')
    const sig = `${name}::${tableColumnSignature(rows)}`
    if (seen.has(sig)) return
    seen.add(sig)
    out.push({
      id: `config-${name}-${iteration ?? out.length}`,
      tool_name: name,
      rows,
    })
  }

  const agentSteps = Array.isArray(responseData.agent_steps) ? responseData.agent_steps : []
  for (const step of agentSteps) {
    if (step?.success === false) continue
    const output = step?.tool_output
    if (output == null) continue
    const unwrapped = unwrapResponseData(typeof output === 'string' ? { output } : output)
    if (unwrapped == null) continue
    const rows = filterApiErrorNoiseRows(normalizeRows(extractRows(unwrapped)))
    push(String(step?.tool_name || 'data'), rows, Number(step?.iteration) || undefined)
  }

  return out.slice(0, 8)
}

const KPI_DEFS: { key: string; label: string; re: RegExp }[] = [
  { key: 'spend', label: 'Spend', re: /\bspend\b/i },
  { key: 'budget', label: 'Budget', re: /\bbudget\b/i },
  { key: 'cpa', label: 'CPA', re: /\bcpa\b|cost[_\s-]?per[_\s-]?(acq|acquisition|conversion)/i },
  { key: 'conversions', label: 'Conversions', re: /\bconversions?\b/i },
  { key: 'cost', label: 'Cost', re: /\bcost\b/i },
  { key: 'roas', label: 'ROAS', re: /\broas\b/i },
]

function findMatchingColumn(rows: Record<string, any>[], re: RegExp): string | null {
  if (!rows[0] || typeof rows[0] !== 'object') return null
  for (const key of Object.keys(rows[0])) {
    if (NON_DATA_KEYS.has(key.toLowerCase())) continue
    const normalized = key.replace(/[_-]+/g, ' ')
    if (re.test(normalized) || re.test(key)) return key
  }
  return null
}

function metricValueFromColumn(rows: Record<string, any>[], col: string): string | null {
  if (isAdditiveMeasureColumn(col, rows)) {
    const nums = columnNumericValues(rows, col)
    if (!nums.length) return null
    return formatValue(nums.reduce((a, b) => a + b, 0), col)
  }
  const nums = columnNumericValues(rows, col)
  if (nums.length === 1) return formatValue(nums[0], col)
  if (nums.length === rows.length && nums.length > 0) {
    const avg = nums.reduce((a, b) => a + b, 0) / nums.length
    return formatValue(avg, col)
  }
  return null
}

function computedCpa(tables: ExtractedRunTable[]): string | null {
  for (const table of tables) {
    const rows = table.rows
    const spendCol =
      findMatchingColumn(rows, /\bspend\b/i) || findMatchingColumn(rows, /\bcost\b/i)
    const convCol = findMatchingColumn(rows, /\bconversions?\b/i)
    if (!spendCol || !convCol) continue
    const spendNums = columnNumericValues(rows, spendCol)
    const convNums = columnNumericValues(rows, convCol)
    if (spendNums.length !== rows.length || convNums.length !== rows.length) continue
    const spend = spendNums.reduce((a, b) => a + b, 0)
    const conv = convNums.reduce((a, b) => a + b, 0)
    if (!(conv > 0)) continue
    return formatValue(spend / conv, 'cpa')
  }
  return null
}

/** 2–4 headline KPIs from extracted tables (column-name heuristics, not prose). */
export function extractRunHighlightMetrics(
  tables: ExtractedRunTable[],
  max = 4,
): RunHighlightMetric[] {
  if (!tables?.length) return []
  const metrics: RunHighlightMetric[] = []
  const used = new Set<string>()

  for (const def of KPI_DEFS) {
    if (metrics.length >= max) break
    if (used.has(def.key)) continue
    if (def.key === 'cost' && used.has('spend')) continue

    if (def.key === 'cpa') {
      const computed = computedCpa(tables)
      if (computed) {
        used.add(def.key)
        metrics.push({ key: def.key, label: def.label, value: computed })
        continue
      }
    }

    for (const table of tables) {
      const col = findMatchingColumn(table.rows, def.re)
      if (!col) continue
      const value = metricValueFromColumn(table.rows, col)
      if (!value) continue
      used.add(def.key)
      metrics.push({ key: def.key, label: def.label, value })
      break
    }
  }

  if (metrics.length === 0 && tables[0]?.rows?.length) {
    return buildMetrics(tables[0].rows)
      .slice(0, max)
      .map((m, i) => ({
        key: `metric-${i}`,
        label: m.label.replace(/^Total\s+/i, ''),
        value: m.value,
      }))
  }

  return metrics
}

function cleanAlertText(raw: string): string {
  return raw
    .replace(/^>\s?/gm, '')
    .replace(/^#+\s*/, '')
    .replace(/^[-*]\s+/, '')
    .replace(/^[⚠️⚠]\s*/, '')
    .replace(/\*\*/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Conservative alert lines from summary markdown. Does not scrape tables out of prose.
 */
export function extractRunAlerts(summary: string | null | undefined, max = 4): string[] {
  if (!summary || typeof summary !== 'string') return []
  const alerts: string[] = []
  const seen = new Set<string>()

  const push = (raw: string) => {
    const cleaned = cleanAlertText(raw)
    if (!cleaned || cleaned.length < 8 || cleaned.length > 280) return
    const key = cleaned.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    alerts.push(cleaned)
  }

  const quoteBlocks = summary.match(/(?:^>.*\n?)+/gm)
  if (quoteBlocks) {
    for (const block of quoteBlocks) {
      push(block)
      if (alerts.length >= max) return alerts.slice(0, max)
    }
  }

  for (const line of summary.split('\n')) {
    const t = line.trim()
    if (!t || t.startsWith('>')) continue
    const startAlert =
      /^(?:⚠️|⚠)/.test(t) ||
      /^\*\*(?:alert|warning|waste alert)\b/i.test(t) ||
      /^(?:alert|warning|waste alert)\s*[:—-]/i.test(t)
    const inlineAlert = t.length <= 220 && /\b(?:waste alert|anomal(?:y|ies)|flagged)\b/i.test(t)
    if (!startAlert && !inlineAlert) continue
    push(t)
    if (alerts.length >= max) break
  }

  return alerts.slice(0, max)
}

/** Detect tabular rows for a run's response_data (agent summaries skip table unwrap). */
export function extractTableRowsFromResponseData(responseData: any): Record<string, any>[] {
  if (!responseData || typeof responseData !== 'object') return []
  const agentSteps = Array.isArray(responseData.agent_steps) ? responseData.agent_steps : []
  // Prefer nested tool output tables when agent summary exists.
  if (agentSteps.length > 0) {
    for (let i = agentSteps.length - 1; i >= 0; i--) {
      const out = agentSteps[i]?.tool_output
      if (out == null) continue
      const rows = rowsFromToolOutput(out)
      if (isDisplayableTable(rows) && !isConfigOrPaginationTable(rows)) {
        return rows
      }
    }
  }

  const data = unwrapResponseData(responseData)
  if (data == null) return []
  const rows = filterApiErrorNoiseRows(normalizeRows(extractRows(data)))
  if (isConfigOrPaginationTable(rows)) return []
  return rows
}

export function tableColumnsFromRows(
  rows: Record<string, any>[],
  maxCols = 10,
): string[] {
  if (!rows?.length || !rows[0] || typeof rows[0] !== 'object') return []
  return Object.keys(rows[0])
    .filter((k) => {
      if (NON_DATA_KEYS.has(k.toLowerCase())) return false
      const v = rows[0][k]
      return v !== null && v !== undefined
    })
    .slice(0, maxCols)
}

/** Escape a CSV cell. Neutralizes spreadsheet formula injection. */
export function escapeCsvCell(value: unknown): string {
  let raw = value == null ? '' : String(value)
  if (/^[=+\-@\t\r]/.test(raw) && !/^-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(raw)) {
    raw = `'${raw}`
  }
  const needsQuotes = /[",\n\r]/.test(raw) || raw.startsWith("'")
  const escaped = raw.replace(/"/g, '""')
  return needsQuotes ? `"${escaped}"` : escaped
}

/** Build a CSV string from table rows (all rows; human headings). */
export function rowsToCsv(rows: Record<string, any>[], columns?: string[]): string {
  if (!rows?.length) return ''
  const cols = columns?.length ? columns : tableColumnsFromRows(rows, 50)
  if (!cols.length) return ''
  const header = cols.map((c) => escapeCsvCell(formatLabel(c))).join(',')
  const body = rows
    .map((row) => cols.map((c) => escapeCsvCell(formatValue(row[c], c))).join(','))
    .join('\n')
  return `${header}\n${body}\n`
}

export function downloadTextFile(contents: string, filename: string, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob([contents], { type: mime })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

export function slugifyFilenamePart(value: string, max = 40): string {
  return String(value || 'task')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, max) || 'task'
}

export function formatRunDuration(startedAt?: string | null, completedAt?: string | null): string | null {
  if (!startedAt || !completedAt) return null
  const start = new Date(startedAt).getTime()
  const end = new Date(completedAt).getTime()
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null
  const secs = Math.round((end - start) / 1000)
  if (secs < 60) return `${secs}s`
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return s ? `${m}m ${s}s` : `${m}m`
}
